import * as React from "react";
import { SectionShell } from "@/components/design-system/SectionShell";
import { MultiSelect } from "@/components/ui/multi-select";

const DISTRICT_COUNCILS = [
  "DC 1M", "DC 3", "DC 4", "DC 5", "DC 6", "DC 7", "DC 9", "DC 11", "DC 16",
  "DC 21", "DC 30", "DC 35", "DC 51", "DC 57", "DC 58", "DC 77", "DC 82",
  "DC 88", "DC 91", "DC 711",
].map((name) => ({ value: name, label: name }));

const code = `import { MultiSelect } from "@/components/ui/multi-select";

const options = districtCouncils.map((dc) => ({
  value: String(dc.id),
  label: dc.name,
}));

<div className="space-y-2">
  <label htmlFor="worked_in">Worked in</label>
  <MultiSelect
    id="worked_in"
    options={options}
    values={selectedIds.map(String)}
    onChange={(values) => setSelectedIds(values.map(Number))}
    placeholder="Choose District Councils…"
    searchPlaceholder="Type to filter councils…"
    emptyMessage="No District Council matches that."
    selectionLabel="Chosen District Councils"
  />
  <p className="text-xs text-ink-muted">Leave it empty if none applies.</p>
</div>`;

export function MultiSelectSection() {
  const [values, setValues] = React.useState<string[]>(["DC 16", "DC 9"]);
  const [empty, setEmpty] = React.useState<string[]>([]);

  return (
    <SectionShell
      id="multi-select"
      title="Multi-select"
      description={
        <>
          <code>Combobox</code> with the selection made plural: the same popover,
          the same type-to-filter, the same keyboard handling (<code>↑</code> /{" "}
          <code>↓</code> / <code>Enter</code> / <code>Esc</code>). Choosing an
          option does <em>not</em> close the list, because picking several things
          is the point. Each choice appears as a removable chip beneath the
          trigger, and the footer offers <em>Clear all</em>.
        </>
      }
      whenToUse={
        <ul>
          <li>Picking several things from a long list — a few dozen options and up.</li>
          <li>
            Where a checkbox list is right in principle but would take over the
            page — the thirty District Councils on the weekly report form.
          </li>
          <li>When the choices need to stay visible after the list closes.</li>
        </ul>
      }
      whenNotToUse={
        <ul>
          <li>
            For a short list — under a dozen or so, a checkbox grid is easier to
            scan because everything is visible at once.
          </li>
          <li>For choosing exactly one thing — use <code>Combobox</code>.</li>
          <li>For actions rather than values — use <code>DropdownMenu</code>.</li>
        </ul>
      }
      preview={
        <div className="flex flex-wrap items-start gap-8">
          <div className="flex w-64 flex-col gap-2">
            <MultiSelect
              options={DISTRICT_COUNCILS}
              values={values}
              onChange={setValues}
              searchPlaceholder="Filter District Councils…"
              triggerLabel="District Councils"
              selectionLabel="Chosen District Councils"
            />
            <span className="text-xs text-ink-muted">With a selection</span>
          </div>
          <div className="flex w-64 flex-col gap-2">
            <MultiSelect
              options={DISTRICT_COUNCILS}
              values={empty}
              onChange={setEmpty}
              placeholder="Choose District Councils…"
              searchPlaceholder="Filter District Councils…"
              triggerLabel="District Councils"
            />
            <span className="text-xs text-ink-muted">Empty state</span>
          </div>
          <div className="flex w-64 flex-col gap-2">
            <MultiSelect
              options={DISTRICT_COUNCILS}
              values={["DC 16"]}
              onChange={() => {}}
              disabled
              triggerLabel="District Councils"
            />
            <span className="text-xs text-ink-muted">Disabled</span>
          </div>
        </div>
      }
      code={code}
      options={
        <ul className="list-disc pl-5">
          <li>
            The trigger names the choices while there are one or two, then
            switches to &ldquo;N selected&rdquo;. Override with{" "}
            <code>summarize</code>.
          </li>
          <li>
            Pass <code>id</code> so a <code>&lt;label htmlFor&gt;</code> points at
            the trigger — the control is a button, not an <code>&lt;input&gt;</code>.
          </li>
          <li>
            Chips keep the order the option list is in, not the order things were
            picked, so the same selection always reads the same way.
          </li>
          <li>
            Values are strings. Convert on the way in and out when the underlying
            ids are numbers.
          </li>
        </ul>
      }
    />
  );
}
